<template>
  <div class="meditation-container" :style="backgroundStyle">
    <button @click="goBack" class="back-btn">
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" class="back-icon">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
      </svg>
    </button>



    <div class="duration-selector" :class="{ 'disabled': isMeditating }">
      <button 
        v-for="duration in durations" 
        :key="duration.value"
        :data-duration="duration.value"
        @click="selectDuration(duration.value)"
        class="duration-btn"
        :class="{ 
          'active': selectedDuration === duration.value
        }"
      >
        {{ duration.label }}
      </button>
    </div>

    <div class="meditation-icon" :class="{ 'breath-animation-active': isMeditating }">
      <img :src="currentIcon" alt="冥想图标" class="icon-image">
    </div>

    <div class="prompt-text">
      {{ promptText }}
    </div>

    <button @click="toggleMeditation" class="meditate-btn">
      {{ isMeditating ? '结束冥想' : '开始冥想' }}
    </button>

    <div v-if="isMeditating" class="countdown">
      剩余: <span>{{ formattedCountdown }}</span>
    </div>

    
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'

export default {
  name: 'MeditationPage',
  setup() {
    const router = useRouter()
    const isMeditating = ref(false)
    const selectedDuration = ref(3)
    const countdownInterval = ref(null)
    const totalSeconds = ref(180)
    const currentSeconds = ref(180)
    const promptText = ref('准备好开始冥想了吗？')
    const breathCycle = ref(0)

    const durations = [
      { value: 3, label: '3分钟' },
      { value: 5, label: '5分钟' },
      { value: 10, label: '10分钟' }
    ]



    const currentIcon = computed(() => {
      const hour = new Date().getHours()
      if (hour >= 5 && hour < 12) return require('../assets/img/云朵.png')
      else if (hour >= 12 && hour < 18) return require('../assets/img/太阳.png')
      else if (hour >= 18 && hour < 22) return require('../assets/img/月亮.png')
      else return require('../assets/img/星星.png')
    })

    const backgroundStyle = computed(() => {
      const hour = new Date().getHours()
      let gradient = ''
      
      if (hour >= 5 && hour < 12) {
        gradient = 'linear-gradient(135deg, #e0f2fe 0%, #f0f9ff 100%)'
      } else if (hour >= 12 && hour < 18) {
        gradient = 'linear-gradient(135deg, #fef3c7 0%, #fffbeb 100%)'
      } else if (hour >= 18 && hour < 22) {
        gradient = 'linear-gradient(135deg, #e0e7ff 0%, #f5f3ff 100%)'
      } else {
        gradient = 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)'
      }
      
      return {
        background: gradient
      }
    })

    const formattedCountdown = computed(() => {
      const minutes = Math.floor(currentSeconds.value / 60)
      const seconds = currentSeconds.value % 60
      return `${minutes}:${seconds.toString().padStart(2, '0')}`
    })



    function selectDuration(duration) {
      if (isMeditating.value) return
      
      selectedDuration.value = duration
      totalSeconds.value = duration * 60
      currentSeconds.value = totalSeconds.value
    }

    function speakText(text) {
      if ('speechSynthesis' in window) {
        const utterance = new SpeechSynthesisUtterance(text)
        utterance.lang = 'zh-CN'
        utterance.volume = 0.5
        utterance.rate = 0.9
        speechSynthesis.speak(utterance)
      } else {
        promptText.value = text
      }
    }

    function startMeditation() {
      isMeditating.value = true
      breathCycle.value = 0
      currentSeconds.value = totalSeconds.value
      
      speakText('深呼吸，慢慢吸气')
      promptText.value = '深呼吸，慢慢吸气'

      countdownInterval.value = setInterval(() => {
        currentSeconds.value--

        if (currentSeconds.value % 6 === 0) {
          breathCycle.value++
          if (breathCycle.value % 2 === 1) {
            speakText('慢慢吸气')
            promptText.value = '慢慢吸气'
          } else {
            speakText('缓缓呼气')
            promptText.value = '缓缓呼气'
          }
        }

        if (currentSeconds.value <= 0) {
          clearInterval(countdownInterval.value)
          speakText('冥想结束，感觉怎么样？')
          promptText.value = '冥想结束，感觉怎么样？'
          endMeditation()
        }

        if (currentSeconds.value === 10) {
          speakText('冥想即将结束，慢慢回到当下')
          promptText.value = '冥想即将结束，慢慢回到当下'
        }
      }, 1000)
    }

    function endMeditation() {
      isMeditating.value = false
      if (countdownInterval.value) {
        clearInterval(countdownInterval.value)
        countdownInterval.value = null
      }
      promptText.value = '准备好开始冥想了吗？'
    }

    function toggleMeditation() {
      if (isMeditating.value) {
        endMeditation()
      } else {
        startMeditation()
      }
    }

    function goBack() {
      router.back()
    }

    onMounted(() => {
    })

    onUnmounted(() => {
      if (countdownInterval.value) {
        clearInterval(countdownInterval.value)
      }
    })

    return {
      isMeditating,
      selectedDuration,
      durations,
      currentIcon,
      promptText,
      formattedCountdown,
      backgroundStyle,
      selectDuration,
      toggleMeditation,
      goBack
    }
  }
}
</script>

<style scoped>
.meditation-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
  font-family: 'YouSheShaYuFeiTe', Arial, sans-serif;
  background-color: var(--background-color);
  color: var(--text-color);
}

.back-btn {
  position: absolute;
  top: 1.5rem;
  left: 1.5rem;
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  background: var(--white-transparent);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 15px -3px var(--shadow-color);
  border: none;
  cursor: pointer;
  transition: all 0.3s;
}

.back-btn:hover {
  background: var(--white);
  transform: translateX(-3px);
}

.back-icon {
  width: 1.5rem;
  height: 1.5rem;
  color: var(--text-color);
}



.duration-selector {
  position: absolute;
  top: 1.5rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 0.75rem;
  background: var(--white-transparent);
  backdrop-filter: blur(4px);
  border-radius: 9999px;
  padding: 0.5rem 1rem;
  box-shadow: 0 4px 6px -1px var(--shadow-color);
  transition: all 0.3s;
}

.duration-selector.disabled {
  opacity: 0.5;
  pointer-events: none;
}

.duration-btn {
  padding: 0.25rem 1rem;
  border-radius: 9999px;
  border: none;
  cursor: pointer;
  transition: all 0.3s;
}

.duration-btn[data-duration="3"] {
  background: var(--secondary-color);
  color: var(--white);
}

.duration-btn[data-duration="3"]:hover {
  background: var(--primary-color);
}

.duration-btn[data-duration="3"].active {
  background: var(--primary-color);
  color: var(--white);
}

.duration-btn[data-duration="5"] {
  background: var(--primary-color);
  color: var(--white);
}

.duration-btn[data-duration="5"]:hover {
  background: var(--secondary-color);
}

.duration-btn[data-duration="5"].active {
  background: var(--secondary-color);
  color: var(--white);
}

.duration-btn[data-duration="10"] {
  background: var(--success-color);
  color: var(--white);
}

.duration-btn[data-duration="10"]:hover {
  background: var(--primary-color);
}

.duration-btn[data-duration="10"].active {
  background: var(--primary-color);
  color: var(--white);
}

.meditation-icon {
  width: 12rem;
  height: 12.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2rem;
  animation: breath 6s ease-in-out infinite;
}

.meditation-icon.breath-animation-active {
  animation: breath 4s ease-in-out infinite;
}

.icon-image {
  width: 80%;
  height: 80%;
  object-fit: contain;
}

@keyframes breath {
  0% {
    transform: scale(1);
    opacity: 0.9;
  }
  50% {
    transform: scale(1.1);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 0.9;
  }
}

.prompt-text {
  font-size: 1.5rem;
  color: #374151;
  margin-bottom: 2rem;
  animation: text-fade 2s ease-in-out;
}

@keyframes text-fade {
  0% {
    opacity: 0;
    transform: translateY(10px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

.meditate-btn {
  padding: 0.75rem 2rem;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(4px);
  color: #374151;
  font-size: 1.125rem;
  font-weight: 500;
  border: none;
  cursor: pointer;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  transition: all 0.3s;
}

.meditate-btn:hover {
  background: rgba(255, 255, 255, 0.9);
}

.countdown {
  position: absolute;
  bottom: 2.5rem;
  font-size: 1.5rem;
  color: #374151;
}

/* 响应式设计 - 平板设备 */
@media (max-width: 1024px) {
  .meditation-container {
    padding: 1rem;
  }
  
  .back-btn {
    top: 1rem;
    left: 1rem;
    width: 2.5rem;
    height: 2.5rem;
  }
  
  .duration-selector {
    top: 1rem;
    gap: 0.5rem;
    padding: 0.4rem 0.75rem;
  }
  
  .duration-btn {
    padding: 0.2rem 0.75rem;
    font-size: 0.875rem;
  }
  
  .meditation-icon {
    width: 10rem;
    height: 10rem;
    margin-bottom: 1.5rem;
  }
  
  .prompt-text {
    font-size: 1.25rem;
    margin-bottom: 1.5rem;
  }
  
  .meditate-btn {
    padding: 0.625rem 1.5rem;
    font-size: 1rem;
  }
  
  .countdown {
    font-size: 1.25rem;
    bottom: 2rem;
  }
}

/* 响应式设计 - 手机设备 */
@media (max-width: 768px) {
  .meditation-container {
    padding: 0.75rem;
  }
  
  .back-btn {
    top: 0.75rem;
    left: 0.75rem;
    width: 2.25rem;
    height: 2.25rem;
  }
  
  .back-icon {
    width: 1.25rem;
    height: 1.25rem;
  }
  
  .duration-selector {
    top: 0.75rem;
    left: 50%;
    transform: translateX(-50%);
    gap: 0.4rem;
    padding: 0.3rem 0.5rem;
    flex-wrap: wrap;
    justify-content: center;
    max-width: 90%;
  }
  
  .duration-btn {
    padding: 0.2rem 0.6rem;
    font-size: 0.8rem;
  }
  
  .meditation-icon {
    width: 8rem;
    height: 8rem;
    margin-bottom: 1rem;
  }
  
  .icon-image {
    width: 70%;
    height: 70%;
  }
  
  .prompt-text {
    font-size: 1.1rem;
    margin-bottom: 1.25rem;
    text-align: center;
    padding: 0 1rem;
  }
  
  .meditate-btn {
    padding: 0.625rem 1.5rem;
    font-size: 0.95rem;
  }
  
  .countdown {
    font-size: 1.1rem;
    bottom: 1.5rem;
  }
}

/* 响应式设计 - 小屏手机设备 */
@media (max-width: 480px) {
  .meditation-container {
    padding: 0.5rem;
  }
  
  .back-btn {
    top: 0.5rem;
    left: 0.5rem;
    width: 2rem;
    height: 2rem;
  }
  
  .back-icon {
    width: 1.1rem;
    height: 1.1rem;
  }
  
  .duration-selector {
    top: 0.5rem;
    gap: 0.3rem;
    padding: 0.25rem 0.4rem;
  }
  
  .duration-btn {
    padding: 0.15rem 0.5rem;
    font-size: 0.75rem;
  }
  
  .meditation-icon {
    width: 7rem;
    height: 7rem;
    margin-bottom: 0.875rem;
  }
  
  .icon-image {
    width: 65%;
    height: 65%;
  }
  
  .prompt-text {
    font-size: 1rem;
    margin-bottom: 1rem;
    padding: 0 0.75rem;
  }
  
  .meditate-btn {
    padding: 0.5rem 1.25rem;
    font-size: 0.9rem;
  }
  
  .countdown {
    font-size: 1rem;
    bottom: 1.25rem;
  }
}

@media (min-width: 768px) {
  .meditation-icon {
    width: 16rem;
    height: 16rem;
  }

  .prompt-text {
    font-size: 1.5rem;
  }
}
</style>
